import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { settingsAPI } from '@/api'
import { LOGOUT, LOGOUT_FULFILLED, LOGOUT_REJECTED } from '@/features/auth/logoutActionTypes'

/**
 * Fetch organization settings for current tenant
 */
export const fetchOrganizationSettings = createAsyncThunk(
  'settings/fetchOrganizationSettings',
  async (_, { getState, signal, rejectWithValue }) => {
    try {
      return await settingsAPI.getOrganizationSettings({ tenantId: getState().auth?.currentTenantId, signal })
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to load organization settings' }
      )
    }
  }
)

/**
 * Update organization settings
 * @param {Object} updates - Settings to update
 */
export const updateOrganizationSettings = createAsyncThunk(
  'settings/updateOrganizationSettings',
  async (updates, { getState, signal, rejectWithValue }) => {
    try {
      const id = getState().settings?.organizationSettings?.id
      return await settingsAPI.updateOrganizationSettings(id, updates, { tenantId: getState().auth?.currentTenantId, signal })
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to update organization settings' }
      )
    }
  }
)

/**
 * Upload organization logo
 * @param {File} file - Logo image file
 */
export const uploadLogo = createAsyncThunk(
  'settings/uploadLogo',
  async (file, { getState, signal, rejectWithValue }) => {
    try {
      return await settingsAPI.uploadLogo(file, { tenantId: getState().auth?.currentTenantId, signal })
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to upload logo' }
      )
    }
  }
)

export const saveBranding = createAsyncThunk(
  'settings/saveBranding',
  async (draft, { getState, signal, rejectWithValue }) => {
    try {
      return await settingsAPI.saveBranding(draft, { tenantId: getState().auth?.currentTenantId, signal })
    } catch (error) {
      return rejectWithValue(error.apiError || { message: 'Failed to save branding' })
    }
  },
)

/**
 * Fetch tenant authentication settings.
 */
export const fetchAuthenticationSettings = createAsyncThunk(
  'settings/fetchAuthenticationSettings',
  async (_, { rejectWithValue }) => {
    try {
      return await settingsAPI.getAuthenticationSettings()
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to load authentication settings' }
      )
    }
  }
)

/**
 * Update tenant authentication settings.
 * @param {Object} payload
 */
export const updateAuthenticationSettings = createAsyncThunk(
  'settings/updateAuthenticationSettings',
  async (payload, { rejectWithValue }) => {
    try {
      return await settingsAPI.updateAuthenticationSettings(payload)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to update authentication settings' }
      )
    }
  }
)

/**
 * Verify an OAuth provider configuration.
 * @param {string} provider
 */
export const verifyAuthenticationProvider = createAsyncThunk(
  'settings/verifyAuthenticationProvider',
  async (provider, { rejectWithValue }) => {
    try {
      return await settingsAPI.verifyAuthenticationProvider(provider)
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to verify provider configuration' }
      )
    }
  }
)

/**
 * Relock password failsafe.
 */
export const relockAuthenticationPassword = createAsyncThunk(
  'settings/relockAuthenticationPassword',
  async (_, { rejectWithValue }) => {
    try {
      return await settingsAPI.relockAuthenticationPassword()
    } catch (error) {
      return rejectWithValue(
        error.apiError || { message: 'Failed to relock password failsafe' }
      )
    }
  }
)

const initialState = {
  organizationSettings: null,
  organizationRequestId: null,
  organizationMutationRequestId: null,
  loading: false,
  error: null,
  saveSuccess: false,
  authenticationSettings: null,
  authLoading: false,
  authError: null,
  authSaveSuccess: false,
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearSaveSuccess: (state) => {
      state.saveSuccess = false
    },
    clearError: (state) => {
      state.error = null
    },
    clearAuthSaveSuccess: (state) => {
      state.authSaveSuccess = false
    },
    // A failed verify's message belongs to that provider's own
    // ProviderVerifyStatus (it is what gets persisted and re-fetched), not to
    // a page-level banner - so the page clears this right after handling a
    // verify attempt, success or failure (F-12 fix round 1, IMPORTANT 2).
    clearAuthError: (state) => {
      state.authError = null
    },
  },
  extraReducers: (builder) => {
    // Fetch organization settings
    builder
      .addCase(fetchOrganizationSettings.pending, (state, action) => {
        // A refresh during Save may read the old row. The mutation owns state
        // until it completes; its response is the authoritative saved profile.
        if (state.organizationMutationRequestId) return
        state.organizationRequestId = action.meta.requestId
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrganizationSettings.fulfilled, (state, action) => {
        if (state.organizationRequestId !== action.meta.requestId) return
        state.organizationRequestId = null
        state.loading = false
        state.organizationSettings = action.payload
      })
      .addCase(fetchOrganizationSettings.rejected, (state, action) => {
        if (state.organizationRequestId !== action.meta.requestId) return
        state.organizationRequestId = null
        state.loading = false
        state.error = action.payload?.message || 'Failed to load settings'
      })

    // Update organization settings
    builder
      .addCase(updateOrganizationSettings.pending, (state, action) => {
        state.organizationRequestId = null
        state.organizationMutationRequestId = action.meta.requestId
        state.loading = true
        state.error = null
        state.saveSuccess = false
      })
      .addCase(updateOrganizationSettings.fulfilled, (state, action) => {
        if (state.organizationMutationRequestId !== action.meta.requestId) return
        state.organizationMutationRequestId = null
        state.loading = false
        state.organizationSettings = action.payload
        state.saveSuccess = true
      })
      .addCase(updateOrganizationSettings.rejected, (state, action) => {
        if (state.organizationMutationRequestId !== action.meta.requestId) return
        state.organizationMutationRequestId = null
        state.loading = false
        state.error = action.payload?.message || 'Failed to update settings'
      })

    // Upload logo
    builder
      .addCase(uploadLogo.pending, (state, action) => {
        state.organizationRequestId = null
        state.organizationMutationRequestId = action.meta.requestId
        state.loading = true
        state.error = null
      })
      .addCase(uploadLogo.fulfilled, (state, action) => {
        if (state.organizationMutationRequestId !== action.meta.requestId) return
        state.organizationMutationRequestId = null
        state.loading = false
        state.organizationSettings = action.payload
        state.saveSuccess = true
      })
      .addCase(uploadLogo.rejected, (state, action) => {
        if (state.organizationMutationRequestId !== action.meta.requestId) return
        state.organizationMutationRequestId = null
        state.loading = false
        state.error = action.payload?.message || 'Failed to upload logo'
      })

    builder
      .addCase(saveBranding.pending, (state, action) => {
        state.organizationRequestId = null
        state.organizationMutationRequestId = action.meta.requestId
        state.loading = true
        state.error = null
        state.saveSuccess = false
      })
      .addCase(saveBranding.fulfilled, (state, action) => {
        if (state.organizationMutationRequestId !== action.meta.requestId) return
        state.organizationMutationRequestId = null
        state.loading = false
        state.organizationSettings = action.payload
        state.saveSuccess = true
      })
      .addCase(saveBranding.rejected, (state, action) => {
        if (state.organizationMutationRequestId !== action.meta.requestId) return
        state.organizationMutationRequestId = null
        state.loading = false
        state.error = action.payload?.message || 'Failed to save branding'
      })
      // The logoutUser thunk's type prefix is "auth/logout", not
      // "auth/logoutUser" (the old strings here never matched).
      .addCase(LOGOUT, () => initialState)
      .addCase(LOGOUT_FULFILLED, () => initialState)
      .addCase(LOGOUT_REJECTED, () => initialState)
      .addCase('auth/switchTenant/fulfilled', () => initialState)

    // Fetch authentication settings
    builder
      .addCase(fetchAuthenticationSettings.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(fetchAuthenticationSettings.fulfilled, (state, action) => {
        state.authLoading = false
        state.authenticationSettings = action.payload
      })
      .addCase(fetchAuthenticationSettings.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload?.message || 'Failed to load authentication settings'
      })

    // Update authentication settings
    builder
      .addCase(updateAuthenticationSettings.pending, (state) => {
        state.authLoading = true
        state.authError = null
        state.authSaveSuccess = false
      })
      .addCase(updateAuthenticationSettings.fulfilled, (state, action) => {
        state.authLoading = false
        state.authenticationSettings = action.payload
        state.authSaveSuccess = true
      })
      .addCase(updateAuthenticationSettings.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload?.message || 'Failed to update authentication settings'
      })

    // Verify provider
    builder
      .addCase(verifyAuthenticationProvider.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(verifyAuthenticationProvider.fulfilled, (state, action) => {
        state.authLoading = false
        state.authSaveSuccess = true
      })
      .addCase(verifyAuthenticationProvider.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload?.message || 'Failed to verify provider'
      })

    // Relock password failsafe
    builder
      .addCase(relockAuthenticationPassword.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(relockAuthenticationPassword.fulfilled, (state, action) => {
        state.authLoading = false
        if (state.authenticationSettings) {
          state.authenticationSettings = {
            ...state.authenticationSettings,
            ...action.payload,
          }
        }
        state.authSaveSuccess = true
      })
      .addCase(relockAuthenticationPassword.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload?.message || 'Failed to relock password'
      })
  },
})

export const { clearSaveSuccess, clearError, clearAuthSaveSuccess, clearAuthError } = settingsSlice.actions

// Selectors
export const selectOrganizationSettings = (state) => state.settings?.organizationSettings
export const selectSettingsLoading = (state) => state.settings?.loading || false
export const selectSettingsError = (state) => state.settings?.error || null
export const selectSettingsSaveSuccess = (state) => state.settings?.saveSuccess || false
export const selectAuthenticationSettings = (state) => state.settings?.authenticationSettings
export const selectAuthenticationSettingsLoading = (state) => state.settings?.authLoading || false
export const selectAuthenticationSettingsSaveSuccess = (state) => state.settings?.authSaveSuccess || false

export default settingsSlice.reducer
